import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { CardFaceData } from '../../../../global-types';
import { CanvasDataTypes } from '../../../../utils';
import { applyOverlayTextStyles } from '../../utils/apply-overlay-text-styles';
import { positionJsxOverFabric } from '../../utils/position-jsx-over-canvas';
import { toggleOverlayVisibility } from '../../../edit-history/edit-history';
import styles from './overlay-textarea.module.scss';
import { useIsSignAndSend } from '../../../../hooks';
import { fillPhotoZoneWithOverlayText } from '../../utils/fill-photo-text-zone-with-overlay-text';
import { useAppContext } from '../../../../context/app-context';
import { useUIStore } from '../../../../stores/ui';
import { validatePhotoTextZoneHasContent } from '../../utils/validate-photo-text-zone-has-content';
import { useInitializationDataContext } from '../../../../context/data-context';
import { isFloatingToolMenuInteracting } from '../../../../components/floating-tool-menu/floating-tool-menu-host';
import { markPendingOverlayReselect } from './pending-overlay-reselect';

type OverlayTextareaProps = {
  canvasRef: React.RefObject<fabric.Canvas>;
  cardFace: CardFaceData;
};

type SyncOverlayTextAreaProps = {
  canvas: fabric.Canvas;
  fabricTextbox: fabric.Textbox;
};

type onShowOverlayTextareaProps = {
  fabricTextbox: fabric.Textbox;
};

type OnMinTextLimitReachedProps = {
  fabricTextbox: fabric.Textbox;
};

export type OverlayTextHandles = {
  isComposing?: () => boolean;
  onShowOverlayTextarea: (props: onShowOverlayTextareaProps) => {
    overlayTextarea?: HTMLTextAreaElement;
    fabricTextbox?: fabric.Textbox;
  };
  onTextareaBlur: () => {
    overlayTextarea?: HTMLTextAreaElement;
    fabricTextbox?: fabric.Textbox;
  };
  syncOverlayTextArea: (props: SyncOverlayTextAreaProps) => void;
  focus: () => void;
  onMinTextLimitReached: ({ fabricTextbox }: OnMinTextLimitReachedProps) => void;
  isOverlayTextareaVisible: () => boolean;
};

export const OverlayTextarea = forwardRef<OverlayTextHandles, OverlayTextareaProps>(({ canvasRef, cardFace }, ref) => {
  const [activeTextareaId, setActiveTextareaId] = useState<fabric.Textbox | null>(null);
  const [textareaContent, setTextareaContent] = useState<string>('default text');
  const [normalizedZoomLevel, setNormalizedZoomLevel] = useState<number | null>(null);
  const isSignAndSendCard = useIsSignAndSend();
  const overlayTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const overlayContainerRef = React.useRef<HTMLDivElement>(null);
  const fabricTexboxRef = React.useRef<fabric.Textbox | null>(null);
  const isComposingRef = React.useRef(false);
  const parentPhotoTextZoneRef = React.useRef<fabric.Group | null>(null);
  const focusAnimationFrameRef = React.useRef<number | null>(null);
  // PRCM-10897: remember the fabric text box's original `hasBorders` value so
  // we can suppress the fabric selection border while the HTML overlay owns
  // the visible outline, and restore it on blur.
  const previousHasBordersRef = React.useRef<boolean | null>(null);
  // PRCM-10897: fabric hides its controls (delete/rotate/etc.) automatically
  // during enterEditing, but the overlay flow calls exitEditing instead so
  // the HTML overlay can own text input. Save the pre-overlay hasControls
  // value here so we can suppress it while the overlay is visible and
  // restore it on blur.
  const previousHasControlsRef = React.useRef<boolean | null>(null);
  const { appState } = useAppContext();
  const openTextDrawer = useUIStore((state) => state.openTextDrawer);
  const { initializedDataState } = useInitializationDataContext();
  const { isDrawerInteracting } = appState;

  const cancelPendingFocus = () => {
    if (focusAnimationFrameRef.current !== null) {
      cancelAnimationFrame(focusAnimationFrameRef.current);
      focusAnimationFrameRef.current = null;
    }
  };

  // PRCM-11039 follow-up: mobile keyboards can cover a bottom-positioned
  // overlay textarea. iOS Safari/Chrome eventually auto-scroll the caret
  // into view via native caret-tracking, but that behavior is delayed and
  // inconsistent (confirmed via device logs), so we take control of it
  // ourselves: whenever the visualViewport shrinks/moves while the overlay
  // is focused, nudge the page so the textarea stays fully visible above
  // the keyboard.
  const visualViewportCleanupRef = React.useRef<(() => void) | null>(null);
  const revealCorrectionTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollOverlayIntoViewIfObscured = () => {
    const overlayTextarea = overlayTextareaRef.current;
    const overlayContainer = overlayContainerRef.current;
    const viewport = window.visualViewport;
    if (!overlayTextarea || !overlayContainer || !viewport || document.activeElement !== overlayTextarea) {
      return;
    }
    const containerRect = overlayContainer.getBoundingClientRect();
    const visibleBottom = viewport.height + viewport.offsetTop;
    const overlapPx = containerRect.bottom - visibleBottom;
    // Safari/Chrome iOS also show a predictive-text/autofill accessory bar
    // directly above the keyboard (~40-50px) that isn't always reflected in
    // visualViewport at the moment we measure, so pad generously to keep
    // the textarea clear of it, not just the keyboard itself.
    const revealMargin = 56;
    if (overlapPx > 0) {
      // Instant (not smooth) so we land on the final position immediately;
      // Safari's chrome (URL bar / accessory bar) can still animate/settle
      // after this, which is why we schedule a corrective re-check below.
      window.scrollBy({ top: overlapPx + revealMargin, left: 0, behavior: 'auto' });
    }

    // The keyboard accessory bar and Safari's own toolbar can still be
    // animating in/out after our scroll, which can leave a residual gap
    // like the one reported. Re-measure shortly after and correct again if
    // still obscured (idempotent — a no-op once fully revealed).
    if (revealCorrectionTimeoutRef.current !== null) {
      clearTimeout(revealCorrectionTimeoutRef.current);
    }
    revealCorrectionTimeoutRef.current = setTimeout(() => {
      revealCorrectionTimeoutRef.current = null;
      const latestOverlayTextarea = overlayTextareaRef.current;
      const latestOverlayContainer = overlayContainerRef.current;
      const latestViewport = window.visualViewport;
      if (
        !latestOverlayTextarea ||
        !latestOverlayContainer ||
        !latestViewport ||
        document.activeElement !== latestOverlayTextarea
      ) {
        return;
      }
      const latestRect = latestOverlayContainer.getBoundingClientRect();
      const latestVisibleBottom = latestViewport.height + latestViewport.offsetTop;
      const latestOverlap = latestRect.bottom - latestVisibleBottom;
      if (latestOverlap > 0) {
        window.scrollBy({ top: latestOverlap + revealMargin, left: 0, behavior: 'auto' });
      }
    }, 150);
  };

  const attachVisualViewportReveal = () => {
    if (!isMobile || !window.visualViewport || visualViewportCleanupRef.current) {
      return;
    }
    const viewport = window.visualViewport;
    const handleViewportChange = () => scrollOverlayIntoViewIfObscured();
    viewport.addEventListener('resize', handleViewportChange);
    viewport.addEventListener('scroll', handleViewportChange);
    visualViewportCleanupRef.current = () => {
      viewport.removeEventListener('resize', handleViewportChange);
      viewport.removeEventListener('scroll', handleViewportChange);
    };
    // Covers the case where the keyboard is already open (viewport already
    // shrunk) by the time this attaches.
    scrollOverlayIntoViewIfObscured();
  };

  const detachVisualViewportReveal = () => {
    visualViewportCleanupRef.current?.();
    visualViewportCleanupRef.current = null;
    if (revealCorrectionTimeoutRef.current !== null) {
      clearTimeout(revealCorrectionTimeoutRef.current);
      revealCorrectionTimeoutRef.current = null;
    }
  };

  React.useEffect(
    () => () => {
      if (focusAnimationFrameRef.current !== null) {
        cancelAnimationFrame(focusAnimationFrameRef.current);
      }
      detachVisualViewportReveal();
    },
    [],
  );

  // Updates the overlay textarea styles, position, and content based on the fabric textbox
  const syncOverlayTextArea = ({ canvas, fabricTextbox }: SyncOverlayTextAreaProps) => {
    const overlayTextarea = overlayTextareaRef.current;
    const overlayContainer = overlayContainerRef.current;
    // Get canvas zoom level
    let canvasZoom = canvas.getZoom();
    if (!canvasZoom || !overlayTextarea || !overlayContainer) {
      throw new Error('Canvas zoom or overlayTextarea missing when syncing overlay styles');
    }

    // For landscape cards the zoom doubles after normalizing. So we need to ensure the zoom
    // level remains consistent on to be in sync with the canvas.
    if (canvasZoom !== normalizedZoomLevel && normalizedZoomLevel) {
      canvasZoom = normalizedZoomLevel;
    }

    // Get text from fabric textbox
    const text = fabricTextbox.text ?? textareaContent;

    // Set text to the current value of the textarea
    setTextareaContent(text);

    // send the phototextzone inteasd of fabricTextbox for sign and send
    // Set overlay position
    positionJsxOverFabric({
      fabricObject: parentPhotoTextZoneRef.current || fabricTextbox,
      element: overlayContainer,
    });

    // Apply text styles to text areas
    applyOverlayTextStyles({
      overlayTextarea,
      overlayContainer,
      fabricTextbox,
      canvasZoom,
      isPhotoTextZone: parentPhotoTextZoneRef.current ? true : false,
      isMobile,
    });

    // Return the overlay textarea and fabric textbox for further use (mainly for testing)
    return { fabricTextbox, overlayTextarea, parentPhotoTextZoneRef };
  };

  // Callback to trigger when text limits have been reached and recent changes needto be reversed
  const onMinTextLimitReached = ({ fabricTextbox }: OnMinTextLimitReachedProps) => {
    // External function to handle when the minimum text limit is reached
    if (!fabricTextbox) {
      throw new Error('err_canvas_or_textbox_not_found');
    }
    // If the textbox is not defined, return empty string to prevent errors (shouldn't be possible)
    const textareaContent = fabricTextbox.text ?? '';
    setTextareaContent(textareaContent);

    // Return the current textarea content for use and testing
    return textareaContent;
  };

  // Triggers when the textbox is clicked.  Should be tied to the canvas event
  const onShowOverlayTextarea = ({ fabricTextbox }: onShowOverlayTextareaProps) => {
    const canvas = canvasRef?.current;
    const overlayTextarea = overlayTextareaRef?.current;
    const overlayContainer = overlayContainerRef?.current;
    const defaultText = initializedDataState.data?.variables.template_data.DefaultTextArea.Text || '';
    // If the canvas, textboxID, or overlayTextarea are not defined, return object with nulls
    if (!canvas || !overlayTextarea || !overlayContainer || !fabricTextbox?.data?.ID) {
      // Log warning with diagnostic details instead of throwing to avoid flooding Datadog errors
      // eslint-disable-next-line no-console
      console.warn('[OverlayTextarea] Cannot show overlay textarea. Missing required dependencies:', {
        hasCanvas: !!canvas,
        hasOverlayTextarea: !!overlayTextarea,
        hasOverlayContainer: !!overlayContainer,
        hasFabricTextbox: !!fabricTextbox,
        fabricTextboxID: fabricTextbox?.data?.ID,
        fabricTextboxType: fabricTextbox?.data?.type,
      });
      return { overlayTextarea: undefined, fabricTextbox: undefined };
    }

    // Used to set zoom level to fix text size issues on landscape cards
    if (!normalizedZoomLevel) {
      setNormalizedZoomLevel(canvas.getZoom());
    }

    fabricTexboxRef.current = fabricTextbox;

    parentPhotoTextZoneRef.current = canvas
      .getObjects()
      .find(
        (obj) => obj.data?.type === CanvasDataTypes.PhotoTextZone && obj.data.ID === fabricTextbox.data.ID,
      ) as fabric.Group;

    // Set text alignment based on data type.
    const textboxType: CanvasDataTypes = fabricTextbox.data.type;
    overlayTextarea.style.alignContent = textboxType === CanvasDataTypes.EditableText ? 'center' : 'start';

    // Exit Fabric editing; the HTML overlay owns text input.
    fabricTextbox.exitEditing();
    canvas.renderAll();

    // Show BEFORE positioning: positionJsxOverFabric uses offsetParent to
    // compensate for the S&S inside-right 0.9x-shrink offset, and
    // `display: none` gives `offsetParent === null` → no compensation.
    toggleOverlayVisibility({
      shouldShowOverlay: true,
      fabricObject: fabricTextbox,
      overlayElement: overlayContainer,
      textareaElement: overlayTextarea,
    });

    // Syncs the overlay textarea with the fabric textbox
    syncOverlayTextArea({
      canvas,
      fabricTextbox,
    });

    const text = fabricTextbox.text && fabricTextbox.text !== defaultText ? fabricTextbox.text : '';
    // Set textarea contents and activeID to state
    setTextareaContent(text);
    overlayTextarea.value = text;
    setActiveTextareaId(fabricTextbox.data.ID);
    if (parentPhotoTextZoneRef.current) {
      openTextDrawer();
    } else if (fabricTextbox.data.type !== CanvasDataTypes.EditableText) {
      canvas.discardActiveObject();
    }

    // PRCM-10897: while the HTML overlay is visible it owns the outline.
    // Suppress the fabric selection border so we don't render a second
    // dashed line inside the overlay's outline. Only applies to code paths
    // that don't discardActiveObject above (photo-text-zone and
    // EditableText), since a discarded object draws no selection border.
    if (previousHasBordersRef.current === null) {
      previousHasBordersRef.current = fabricTextbox.hasBorders ?? true;
    }
    fabricTextbox.hasBorders = false;
    // PRCM-10897: fabric's own edit mode hides controls (delete/rotate) via
    // enterEditing→_setEditingProps. The overlay flow calls exitEditing, so
    // controls stay visible unless we suppress them here — which showed the
    // trash icon while the user was actively typing.
    if (previousHasControlsRef.current === null) {
      previousHasControlsRef.current = fabricTextbox.hasControls ?? true;
    }
    fabricTextbox.hasControls = false;
    canvas.renderAll();

    const focusOverlayTextarea = () => {
      if (overlayTextareaRef.current !== overlayTextarea || overlayContainer.style.display === 'none') {
        return;
      }
      overlayTextarea.focus({ preventScroll: true });
      const cursorPosition = overlayTextarea.value.length;
      overlayTextarea.setSelectionRange(cursorPosition, cursorPosition);
    };
    cancelPendingFocus();
    focusOverlayTextarea();
    attachVisualViewportReveal();
    focusAnimationFrameRef.current = requestAnimationFrame(() => {
      focusAnimationFrameRef.current = null;
      focusOverlayTextarea();
    });

    return { overlayTextarea: overlayTextareaRef.current || null, fabricTextbox };
  };

  // Triggers when the textarea overlay is blurred
  const onTextareaBlur = () => {
    // Ensure dependencies are all here
    const canvasObjects = canvasRef?.current?.getObjects();
    const activeTextbox = activeTextareaId;
    const overlayTextarea = overlayTextareaRef?.current;
    const overlayContainer = overlayContainerRef?.current;
    if (
      !canvasRef ||
      activeTextareaId === null ||
      !canvasObjects ||
      !activeTextbox ||
      !overlayTextarea ||
      !overlayContainer
    ) {
      return {
        overlayTextarea: overlayTextareaRef.current || undefined,
        fabricTextbox: fabricTexboxRef.current || undefined,
      };
    }

    // Reset these to default to ensure logic doens't bleed into next selected textbox
    setActiveTextareaId(null);
    setNormalizedZoomLevel(null);

    // Find the fabric textbox with the same ID as the activeTextbox
    const fabricTextbox = fabricTexboxRef.current;
    // If the fabric textbox is not found, return
    if (!fabricTextbox) {
      throw new Error('No fabric textbox found with the active ID');
    }
    cancelPendingFocus();
    detachVisualViewportReveal();
    // Set textbox to use textarea contents
    // fabricTextbox.text = textareaContent;

    if (isSignAndSendCard) {
      validatePhotoTextZoneHasContent({
        canvas: canvasRef.current as fabric.Canvas,
        textBox: fabricTextbox,
        photoTextZone: parentPhotoTextZoneRef.current as fabric.Group,
      });
    }

    toggleOverlayVisibility({
      shouldShowOverlay: false,
      fabricObject: fabricTextbox,
      overlayElement: overlayContainer,
      textareaElement: overlayTextarea,
    });

    // PRCM-10897: overlay closed → fabric object owns the visible outline
    // again if it becomes selected, so restore hasBorders and hasControls
    // to whatever they were before the overlay opened.
    if (previousHasBordersRef.current !== null) {
      fabricTextbox.hasBorders = previousHasBordersRef.current;
      previousHasBordersRef.current = null;
    }
    if (previousHasControlsRef.current !== null) {
      fabricTextbox.hasControls = previousHasControlsRef.current;
      previousHasControlsRef.current = null;
    }

    fabricTextbox.fire('editing:exited', { target: fabricTextbox });

    canvasRef?.current?.renderAll();
    // Check if blur was caused by drawer interaction

    if (isDrawerInteracting || isFloatingToolMenuInteracting()) {
      return {
        overlayTextarea: overlayTextareaRef.current || undefined,
        fabricTextbox: fabricTexboxRef.current || undefined,
      };
    }

    // PRCM-10837: POD has a SELECT mode after edit; S&S does not. Also skip
    // EditableText (template textareas) — they get deleted rather than
    // reselected, so marking the flag without a rAF reselect would make the
    // outside-click handler skip its discard branch and leave the UI stuck.
    if (!isSignAndSendCard && fabricTextbox.data?.type !== CanvasDataTypes.EditableText) {
      // Mark BEFORE the rAF so handleMouseUp / handleClickOutsideCanvas can
      // skip their discard branches. Do NOT reset inside the rAF — on desktop,
      // native blur runs during mousedown, so the rAF fires BEFORE mouseup.
      // The flag must survive until the outer click event; the module
      // auto-clears with a short timeout as a safety net.
      markPendingOverlayReselect();
      requestAnimationFrame(() => {
        canvasRef.current?.setActiveObject(fabricTextbox);
        // fabric's setActiveObject does not render on its own (PRCM-10837).
        canvasRef.current?.renderAll();
      });
    }

    // trigger the changed event to check bound of the textbox within the photo text zone
    if (isSignAndSendCard) {
      fabricTexboxRef.current?.fire('changed', {
        target: fabricTexboxRef.current,
      });
    }

    // Return the manipulated elements. Useful for testing purposes.
    return { overlayTextarea, fabricTextbox };
  };

  // Exposes the above methods to the parent component
  useImperativeHandle(ref, () => ({
    isComposing: () => isComposingRef.current,
    onShowOverlayTextarea,
    onTextareaBlur,
    syncOverlayTextArea,
    focus: () => {
      overlayTextareaRef.current?.focus();
    },
    onMinTextLimitReached,
    isOverlayTextareaVisible: () => {
      const overlayTextarea = overlayTextareaRef.current;
      const overlayContainer = overlayContainerRef.current;
      return overlayTextarea && overlayContainer ? overlayContainer.style.display !== 'none' : false;
    },
  }));

  const onTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Get all dependencie
    const fabricTextbox = fabricTexboxRef.current;
    const overlayTextarea = overlayTextareaRef.current;
    const canvas = canvasRef.current;
    const text = e.target.value;
    if (!fabricTextbox || !overlayTextarea || !canvas) {
      throw new Error('err_canvas_or_textbox_not_found');
    }
    // Set fabric textbox value to be new textarea
    const textChanged = fabricTextbox.text !== text;
    fabricTextbox.set({ text });
    if (textChanged && fabricTextbox.data?.type === CanvasDataTypes.UserText) {
      fabricTextbox.set('data', { ...fabricTextbox.data, edited: true, isEdited: true });
    }

    canvas.renderAll();
    if (isMobile) {
      scrollOverlayIntoViewIfObscured();
    }

    // Get cursor position on textarea
    const cursorPosition = overlayTextarea.selectionStart;
    fabricTextbox.setSelectionEnd(cursorPosition);
    // Set the textarea content to the new value
    setTextareaContent(e.target.value);

    // If sign and send, fill the photo text zone with the overlay text and hide the buttons
    if (isSignAndSendCard) {
      fillPhotoZoneWithOverlayText({
        canvas: canvasRef.current as fabric.Canvas,
        textBox: fabricTextbox,
        photoTextZone: parentPhotoTextZoneRef.current as fabric.Group,
        text,
      });
    }

    // Fire textarea change event, which triggers our resize logic
    canvas.fire('text:changed', {
      target: fabricTextbox,
    });

    canvas.renderAll();

    e.preventDefault();
    e.stopPropagation();
  };

  const textareaId = cardFace?.type ? `overlay-textarea-${cardFace.type}` : 'overlay-textarea';
  // PRCM-10897: match region convention for the active-editing outline
  // (UK charcoal / US Hallmark purple).
  return (
    <>
      <div
        className={`${styles['overlay-container']} ${initializedDataState.isUK ? styles['overlay-container-uk'] : ''}`}
        data-testid={`${textareaId}-container`}
        ref={overlayContainerRef}
      >
        <textarea
          id={textareaId}
          data-testid={textareaId}
          className={styles['overlay-textarea']}
          value={textareaContent}
          rows={1}
          wrap="off"
          spellCheck="true"
          autoCorrect="on"
          ref={overlayTextareaRef}
          onInput={onTextAreaChange}
          onCompositionStart={() => {
            isComposingRef.current = true;
            canvasRef.current?.fire('history:composition');
          }}
          onCompositionEnd={(event) => {
            fabricTexboxRef.current?.set({ text: event.currentTarget.value });
            setTextareaContent(event.currentTarget.value);
            isComposingRef.current = false;
            canvasRef.current?.fire('history:composition');
          }}
          onBlur={onTextareaBlur}
        />
      </div>
    </>
  );
});
